import { Formik } from "formik";
import React, { useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2 } from '@blueprint/ui';
import { useModal } from "react-modal-hook";
import * as yup from "yup";

import bankAccountApi, { useBankAccountById } from "../../../api/bankAccount";
import { Loader, toast } from "../../../common/components/core";
import { TextareaField } from "../../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import { GenericError, composeErrorHandlers } from "../../../utils/errorHandling";
import { FormSubmissionHandler, BankStatus } from "../../../types/common";

export type SARejectFormValues = {
    comment: string;
};

const schema: yup.SchemaOf<SARejectFormValues> = yup.object().shape({
    comment: yup.string().required(),
});

type ReviewRejectHandler = {
    isSubmitting: boolean;
    handler: (
        accountType: string,
        accountTypeLow: string,
        status: BankStatus | undefined,
    ) => void;
};

const useReviewRejectHandler = (accountId: string): ReviewRejectHandler => {
    const [ isSubmitting, setSubmitting ] = useState(false);
    const [ accountType, setAccountType ] = useState<string>("");
    const [ accountTypeLow, setAccountTypeLow ] = useState<string>("");
    const [ status, setStatus ] = useState<BankStatus>();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const { mutate } = useBankAccountById(accountId);
    const typeText = status === BankStatus.PENDING_APPROVAL ? "Set up" : "Edit";
    const typeTextLow = status === BankStatus.PENDING_APPROVAL ? "set up" : "edit";

    const [show, hide] = useModal(() => {
        const confirm: FormSubmissionHandler<SARejectFormValues> = async (values) => {
            setSubmitting(true);
            try {
                const res = await bankAccountApi.review(accountId, 1, values.comment);
                mutate(res);
                toast(`${accountType} account ${typeTextLow} rejected`);
            } catch (e) {
                composeErrorHandlers(
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            hide();
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Formik
                    validationSchema={schema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={confirm}
                    initialValues={{
                        comment: "",
                    }}
                >
                    {({ handleSubmit }) => (
                        <Loader overlay isLoading={isSubmitting}>
                            <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <Modal.Header>
                                    <h2 className="mb-0">
                                        {`Reject ${accountType} Account ${typeText}`}
                                    </h2>
                                </Modal.Header>
                                <Modal.Body>
                                    <p>{`Are you sure you want to reject this ${accountTypeLow} account ${typeTextLow}?`}</p>
                                    <TextareaField
                                        name="comment"
                                        label="Comment"
                                        maxLength={256}
                                        required={true}
                                        isAllowed={(formattedValue) => formattedValue.length < 256}
                                    />
                                </Modal.Body>

                                <Modal.Footer className="d-flex justify-content-between px-3">
                                    <Button variant="light.tertiary" onClick={hide}>
                                        Cancel
                                    </Button>
                                    <Button variant="light.primary" type="submit">
                                        Confirm
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [accountId, mutate, isSubmitting, accountType, accountTypeLow, status, onAuthError]);

    return {
        isSubmitting,
        handler: (accountType, accountTypeLow, status) => {
            setAccountType(accountType);
            setAccountTypeLow(accountTypeLow);
            setStatus(status);
            show();
        },
    };
};

export default useReviewRejectHandler;
